import React, { useState, useEffect, useRef } from "react";
import { useApi } from "../../../hooks/use-api";
import { useQuestionBankHandler } from "../handler";
import "./style.sass";

export const ChoosePopup = ({ onClose, onProceed }) => {
  const [step, setStep] = useState(1);
  const [selectedOption, setSelectedOption] = useState("question_bank");

  // Step 2 form state
  const [testName, setTestName] = useState("");
  const [noOfQuestions, setNoOfQuestions] = useState("");
  const [testList, setTestList] = useState([]);
  const [loadingTests, setLoadingTests] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const dropdownRef = useRef(null);
  const api = useApi();
  const { fetchTeacherTestList } = useQuestionBankHandler(api);

  // Close dropdown menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch test list from API: GET /teacher/test/active-list?email=...&role=teacher&page=1&page_size=50
  useEffect(() => {
    let isMounted = true;
    const loadTests = async () => {
      setLoadingTests(true);
      try {
        const res = await fetchTeacherTestList({ page: 1, pageSize: 50 });
        if (isMounted && res) {
          const list = Array.isArray(res)
            ? res
            : Array.isArray(res.tests)
            ? res.tests
            : Array.isArray(res.active_tests)
            ? res.active_tests
            : Array.isArray(res.data)
            ? res.data
            : [];
          setTestList(list);
        }
      } catch (err) {
        console.error("Error loading test list:", err);
      } finally {
        if (isMounted) setLoadingTests(false);
      }
    };

    loadTests();
  }, []);

  const options = [
    {
      id: "question_bank",
      title: "Choose question from the question bank",
      description: "Browse and select questions directly from the structured question bank.",
      badge: "QUESTION BANK",
      theme: "green",
      headerLineClass: "header_line_green",
      badgeClass: "green",
      bannerBgClass: "green_bg",
      icon: (
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      ),
    },
    {
      id: "ai_generated",
      title: "Choose question from the AI generated questions",
      description: "Select questions from the pool of 45000+ AI generated & curated questions.",
      badge: "AI POWERED",
      theme: "purple",
      headerLineClass: "header_line_purple",
      badgeClass: "purple",
      bannerBgClass: "purple_bg",
      icon: (
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
          <path d="M20 3v4M18 5h4" />
        </svg>
      ),
    },
  ];

  const handleStep1Proceed = () => {
    if (!selectedOption) return;
    setStep(2);
  };

  const handleFinalProceed = () => {
    if (!testName.trim()) return;
    const selectedTestObj = testList.find(
      (t) => (t.test_name || t.testName || "").trim() === testName.trim()
    );

    const selectedOptObj = options.find((opt) => opt.id === selectedOption);

    const testSubTopic =
      selectedTestObj?.sub_topic ??
      selectedTestObj?.subTopic ??
      selectedTestObj?.sub_topics ??
      "";

    onProceed({
      optionType: selectedOption,
      testName: testName.trim(),
      noOfQuestions: noOfQuestions ? parseInt(noOfQuestions, 10) : 10,
      exam: selectedTestObj?.exam || "NEET",
      subject: selectedTestObj?.subject || "Biology",
      className: selectedTestObj?.class || "Class 11",
      chapter: selectedTestObj?.chapter || "",
      subTopic: testSubTopic,
      sub_topic: testSubTopic,
      selectedTest: selectedTestObj,
      title: selectedOptObj?.title,
    });
  };

  // Filter test list by typed search query
  const filteredTests = testList.filter((t) => {
    const name = t.test_name || t.testName || "";
    return name.toLowerCase().includes((testName || "").toLowerCase());
  });

  return (
    <div className="choose_popup_overlay" onClick={onClose}>
      <div className={`popup_box ${step === 2 ? "step2_box" : ""}`} onClick={(e) => e.stopPropagation()}>
        <button className="close_btn" onClick={onClose} aria-label="Close popup">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        {step === 1 ? (
          <>
            <h2>Choose Questions</h2>
            <p className="popup_subtitle">
              Select source to choose questions for your question paper
            </p>

            <div className="qb_cards_selection_grid">
              {options.map((opt) => {
                const isSelected = selectedOption === opt.id;
                return (
                  <div
                    key={opt.id}
                    className={`qb_option_card theme_${opt.theme} ${isSelected ? "selected" : ""}`}
                    onClick={() => setSelectedOption(opt.id)}
                  >
                    <div className={`card_banner ${opt.bannerBgClass}`}>
                      <div className="graphic_mockup">
                        <div className={`mock_line ${opt.headerLineClass}`}></div>
                        <div className="mock_line"></div>
                        <div className="mock_line short"></div>
                        <div className={`banner_badge ${opt.badgeClass}`}>{opt.icon}</div>
                      </div>

                      <div className={`radio_indicator ${isSelected ? "checked" : ""}`}>
                        {isSelected && (
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#ffffff"
                            strokeWidth="3.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        )}
                      </div>
                    </div>

                    <div className="card_info">
                      <span className={`type_badge badge_${opt.theme}`}>{opt.badge}</span>
                      <h4 className="card_title">{opt.title}</h4>
                      <p className="card_desc">{opt.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="button_group">
              <button type="button" className="cancel_btn" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="upload_btn active"
                onClick={handleStep1Proceed}
                disabled={!selectedOption}
              >
                Proceed →
              </button>
            </div>
          </>
        ) : (
          <div className="step2_container">
            <div className="popup_header_center">
              <h2>Select Test Details</h2>
              <p className="popup_subtitle">
                Select test name and enter number of questions to continue
              </p>
            </div>

            <div className="popup_step2_card">
              {/* Test Name Dropdown */}
              <div
                className={`qb_choose_form_group ${dropdownOpen ? "open_dropdown" : ""}`}
                ref={dropdownRef}
              >
                <label>Test Name</label>
                <div className="qb_choose_combobox_container">
                  <svg className="input_left_icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                  <input
                    className="qb_choose_combobox_input with_icon"
                    type="text"
                    placeholder={loadingTests ? "Loading test names..." : "Select or search test name..."}
                    value={testName}
                    onChange={(e) => {
                      setTestName(e.target.value);
                      setDropdownOpen(true);
                    }}
                    onFocus={() => setDropdownOpen(true)}
                    disabled={loadingTests}
                  />
                  <svg
                    className={`qb_choose_combobox_arrow ${dropdownOpen ? "rotated" : ""}`}
                    onClick={() => setDropdownOpen((prev) => !prev)}
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#6B7280"
                    strokeWidth="2.2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>

                  {dropdownOpen && (
                    <div className="qb_choose_combobox_menu">
                      <div className="qb_choose_combobox_options">
                        {filteredTests.map((t, idx) => {
                          const name = t.test_name || t.testName || "";
                          if (!name) return null;
                          const isSelected = testName === name;
                          return (
                            <div
                              key={t.test_id || idx}
                              className={`qb_choose_combobox_item ${isSelected ? "selected" : ""}`}
                              onClick={() => {
                                setTestName(name);
                                setDropdownOpen(false);
                              }}
                            >
                              <span>{name}</span>
                              {isSelected && (
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#612EF7"
                                  strokeWidth="2.5"
                                >
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              )}
                            </div>
                          );
                        })}
                        {filteredTests.length === 0 && !loadingTests && (
                          <div className="qb_choose_combobox_empty">No matching test names</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Number of Questions Input */}
              <div className="qb_choose_form_group">
                <label>Number of Questions</label>
                <div className="input_with_icon_wrapper">
                  <svg className="input_left_icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
                    <line x1="4" y1="9" x2="20" y2="9" />
                    <line x1="4" y1="15" x2="20" y2="15" />
                    <line x1="10" y1="3" x2="8" y2="21" />
                    <line x1="16" y1="3" x2="14" y2="21" />
                  </svg>
                  <input
                    className="qb_choose_number_input with_icon"
                    type="number"
                    min="1"
                    max="500"
                    placeholder="Enter number of questions (e.g. 10)"
                    value={noOfQuestions}
                    onChange={(e) => setNoOfQuestions(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="button_group">
              <button type="button" className="cancel_btn" onClick={() => setStep(1)}>
                ← Back
              </button>
              <button
                type="button"
                className={`upload_btn ${testName.trim() ? "active" : ""}`}
                onClick={handleFinalProceed}
                disabled={!testName.trim()}
              >
                Proceed →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
